import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Eyebrow, Cta } from "../../components/site/kit";
import { Reveal } from "../../components/site/motion";
import { useBlogs } from "../../lib/blogsApi";
import { useHomeCopy } from "../../lib/siteContent";
import { RichCopy } from "../../components/site/RichCopy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Insights = () => {
  const blogs = useBlogs();
  const copy = useHomeCopy();
  const [visible, setVisible] = useState(3);
  const [order, setOrder] = useState([]);
  const [topSlug, setTopSlug] = useState(null);

  useEffect(() => {
    fetch(`${API}/insights/popular?limit=8`)
      .then((r) => r.json())
      .then((d) => {
        const slugs = (d.blogs || []).map((b) => b.slug);
        setOrder(slugs);
        const top = (d.blogs || [])[0];
        if (top && top.views > 0) setTopSlug(top.slug);
      })
      .catch(() => {});
  }, []);

  const ordered = useMemo(() => {
    const rank = new Map(order.map((s, i) => [s, i]));
    const sorted = order.length
      ? [...blogs].sort((a, b) => {
          const ra = rank.has(a.slug) ? rank.get(a.slug) : Infinity;
          const rb = rank.has(b.slug) ? rank.get(b.slug) : Infinity;
          return ra - rb;
        })
      : [...blogs];
    const fi = sorted.findIndex((b) => b.featured);
    if (fi > 0) sorted.unshift(sorted.splice(fi, 1)[0]);
    return sorted;
  }, [blogs, order]);

  const shown = ordered.slice(0, visible);
  const allShown = visible >= ordered.length;
  return (
  <section data-testid="home-insights" className="border-b border-black/10 bg-[#F7F6F2]">
    <div className="mx-auto max-w-[1360px] px-6 py-24 md:px-12 md:py-32 lg:px-16">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>{copy.insightsEyebrow}</Eyebrow>
          <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-black md:text-5xl lg:text-6xl">
            <RichCopy text={copy.insightsHeading} />
          </h2>
        </div>
        <Cta to="/insights" testId="home-insights-cta">All insights</Cta>
      </div>

      <Reveal className="mt-14 border-t border-black/15">
        {shown.map((b) => (
          <Link key={b.slug} to={`/insights/${b.slug}`} className="group grid items-center gap-4 border-b border-black/15 py-8 transition-colors duration-200 hover:bg-black/[0.03] md:grid-cols-12 md:gap-8">
            <div className="flex items-center gap-4 md:col-span-3">
              <span className="font-display text-2xl leading-none text-[#5f8f10] md:text-3xl">{b.number}</span>
              {b.featured ? (
                <span data-testid="home-featured-badge" className="inline-flex items-center gap-1.5 border border-[#5f8f10] bg-[#161A15] px-2 py-1 font-body text-[10px] font-semibold uppercase tracking-[0.14em] text-[#5f8f10]">Featured</span>
              ) : b.slug === topSlug ? (
                <span data-testid="home-most-read-badge" className="inline-flex items-center gap-1.5 bg-[#5f8f10] px-2 py-1 font-body text-[10px] font-semibold uppercase tracking-[0.14em] text-[#161A15]">Most read</span>
              ) : (
                <span className="font-body text-xs font-semibold uppercase tracking-[0.1em] text-black/40">{b.tags.join(" · ")}</span>
              )}
            </div>
            <div className="md:col-span-7">
              <h3 className="font-display text-xl uppercase tracking-tight text-black transition-colors duration-200 group-hover:text-[#5f8f10] md:text-2xl">{b.title}</h3>
              <p className="font-body mt-2 text-base font-normal text-black/70">{b.excerpt}</p>
            </div>
            <span className="font-body md:col-span-2 md:text-right text-sm font-semibold uppercase tracking-[0.14em] text-black">Read <span aria-hidden className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span></span>
          </Link>
        ))}
      </Reveal>
      <div className="mt-8 flex justify-center">
        {allShown ? (
          <Link to="/insights" data-testid="home-insights-readmore" className="group inline-flex items-center gap-2 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#5f8f10] transition-colors">
            Read more on the Switchboard
            <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-1">→</span>
          </Link>
        ) : (
          <button type="button" onClick={() => setVisible((v) => v + 3)} data-testid="home-insights-loadmore" className="group inline-flex items-center gap-2 font-body text-sm font-semibold uppercase tracking-[0.16em] text-black transition-colors hover:text-[#5f8f10]">
            Load more
            <span aria-hidden className="transition-transform duration-200 group-hover:translate-y-0.5">↓</span>
          </button>
        )}
      </div>
    </div>
  </section>
  );
};

